import { z } from "zod";
import { IATA_CODE_PATTERN } from "./travel";

/**
 * `/airports` — the world airport list (migration 0087, user 2026-11-03).
 *
 * Source: OurAirports (public domain,
 * https://davidmegginson.github.io/ourairports-data/airports.csv), loaded by
 * `pnpm --filter @seagames/api airports:import` — NEVER the flight API
 * (AeroDataBox has no list-all endpoint and a 400-unit monthly quota). Only
 * rows with an IATA code and an airport type below are kept; a row that
 * leaves the file is deactivated, never deleted. Wire shapes are snake_case.
 */

/** Our size word, from OurAirports' `type`. Also the ordering in the picker (LARGE first). */
export const AIRPORT_SIZES = ["LARGE", "MEDIUM", "SMALL"] as const;
export type AirportSize = (typeof AIRPORT_SIZES)[number];

/** OurAirports `type` → our size. Any other type (heliport, seaplane_base, closed…) is not imported. */
export const OURAIRPORTS_TYPE_TO_SIZE: Readonly<Record<string, AirportSize>> = {
	large_airport: "LARGE",
	medium_airport: "MEDIUM",
	small_airport: "SMALL",
};

/** An ICAO airport code: `WMKK`. `ck_airports_icao_code`. Plain classes only (written into the CHECK). */
export const AIRPORT_ICAO_PATTERN = /^[A-Z0-9]{4}$/;
/** ISO 3166-1 alpha-2, as OurAirports' `iso_country`: `MY`. `ck_airports_country_code`, `ck_airlines_country_code`. */
export const COUNTRY_ISO2_PATTERN = /^[A-Z]{2}$/;

/** `GET /airports/options`: the picker's search. `q` is trimmed BEFORE the length check (bug 5). */
export const AIRPORT_OPTIONS_Q_MIN = 2;
export const AIRPORT_OPTIONS_Q_MAX = 100;
/** Below this length the name / city must START with q (no trigram to probe for "contains"); IATA prefix still applies. */
export const AIRPORT_OPTIONS_CONTAINS_MIN = 3;
export const AIRPORT_OPTIONS_LIMIT_DEFAULT = 20;
export const AIRPORT_OPTIONS_LIMIT_MAX = 50;

export const airportOptionsQuery = z.object({
	q: z.string().trim().min(AIRPORT_OPTIONS_Q_MIN).max(AIRPORT_OPTIONS_Q_MAX),
	limit: z.coerce.number().int().min(1).max(AIRPORT_OPTIONS_LIMIT_MAX).default(AIRPORT_OPTIONS_LIMIT_DEFAULT),
});
export type AirportOptionsQuery = z.infer<typeof airportOptionsQuery>;

export const airportIataParam = z.object({ iata: z.string().trim().toUpperCase().regex(IATA_CODE_PATTERN) });

/** One row of `GET /airports/options`. */
export interface AirportOption {
	iata_code: string;
	icao_code: string | null;
	name: string;
	/** OurAirports `municipality`. */
	city: string | null;
	/** ISO 3166-1 alpha-2. */
	country_code: string;
	/** IANA zone computed from the coordinates at import (OurAirports has none). Null when the point fell in no zone. */
	time_zone: string | null;
	size: AirportSize;
}

/** `GET /airports/:iata`. */
export interface AirportDetail extends AirportOption {
	latitude: number;
	longitude: number;
	scheduled_service: boolean;
	updated_at: string;
}
